import type { ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { Storage } from '@frameio/utils';
import {
  type HydrateProjectsQuery,
  useHydrateProjectsQuery,
} from '../../projects/gql/generated/HydrateProjects';

export const RecentProjectStorage = new Storage<{
  recentProjects: Record<string, string[]>;
}>();

const RecentProjectsContext = createContext<{
  loading: boolean;
  removeRecentProjects: (projectIds: string[]) => void;
  addRecentProject: (projectId: string) => void;
  storageIds: Set<string>;
  recentProjects?: NonNullable<
    NonNullable<HydrateProjectsQuery['account']>['projects']
  >['nodes'];
}>({
  addRecentProject: noop,
  removeRecentProjects: noop,
  loading: false,
  storageIds: new Set(),
  recentProjects: undefined,
});

export const RecentProjectsProvider = memo(
  ({ children }: { children: ReactNode }) => {
    const accountId = useActiveAccountId();
    const [idsState, setIdsState] = useState<string[]>([]);

    const { data, loading, error } = useHydrateProjectsQuery({
      variables: { accountId: accountId ?? '', projectIds: idsState },
      skip: !accountId || !idsState?.length,
    });

    const unarchivedProjects = useMemo(() => {
      const idToIndex = new Map<string, number>(
        idsState.map((id, index) => [id, index])
      );

      const sortedProjects = [...(data?.account?.projects?.nodes ?? [])].sort(
        (a, b) =>
          (idToIndex.get(a?.id ?? '') ?? -1) -
          (idToIndex.get(b?.id ?? '') ?? -1)
      );
      return (
        sortedProjects?.filter((project) => project && !project.archivedAt) ??
        []
      );
    }, [data?.account?.projects?.nodes, idsState]);

    useEffect(() => {
      if (error) setStorage(() => [], accountId);
      checkStorageVersion();
      setIdsState(getStorage(accountId));
    }, [accountId, error]);

    const addRecentProject = useCallback(
      (projectId: string) => {
        if (getStorage(accountId).includes(projectId)) {
          setStorage(
            (prev) => [projectId, ...prev.filter((id) => id !== projectId)],
            accountId
          );
        } else {
          setStorage(
            (prev) => [projectId, ...prev].slice(0, MAX_RECENT_PROJECTS),
            accountId
          );
        }
        setIdsState(getStorage(accountId));
      },
      [accountId]
    );

    const removeRecentProjects = useCallback(
      (projectIds: string[]) => {
        const removedIds = new Set(projectIds);
        setStorage(
          (prev) => prev.filter((id) => !removedIds.has(id)),
          accountId
        );
        setIdsState(getStorage(accountId));
      },
      [accountId]
    );

    return (
      <RecentProjectsContext.Provider
        value={useMemo(
          () => ({
            loading,
            removeRecentProjects,
            addRecentProject,
            storageIds: new Set(idsState),
            recentProjects: unarchivedProjects ?? [],
          }),
          [
            addRecentProject,
            idsState,
            loading,
            removeRecentProjects,
            unarchivedProjects,
          ]
        )}
      >
        {children}
      </RecentProjectsContext.Provider>
    );
  }
);

export const useRecentProjects = () =>
  useContextSelector(
    RecentProjectsContext,
    (context) => context.recentProjects
  );

export const useRecentProjectsLoading = () =>
  useContextSelector(RecentProjectsContext, (context) => context.loading);

export const useAddRecentProject = () =>
  useContextSelector(
    RecentProjectsContext,
    (context) => context.addRecentProject
  );
export const useRemoveRecentProjects = () =>
  useContextSelector(
    RecentProjectsContext,
    (context) => context.removeRecentProjects
  );

export const useRecentProjectsCount = () =>
  useContextSelector(
    RecentProjectsContext,
    (context) => context.storageIds.size
  );

export const useRecentProjectsStorageIds = () =>
  useContextSelector(RecentProjectsContext, (context) => context.storageIds);

const MAX_RECENT_PROJECTS = 6;

const getStorage = (accountId: string) => {
  const storage = RecentProjectStorage.get('recentProjects', {})!;
  return Array.isArray(storage[accountId]) ? storage[accountId] : [];
};

const setStorage = (
  setter: (prev: string[]) => string[],
  accountId: string
) => {
  let storage = RecentProjectStorage.get('recentProjects', {})!;
  if (!Array.isArray(storage[accountId])) {
    RecentProjectStorage.set('recentProjects', { ...storage, [accountId]: [] });
    storage = RecentProjectStorage.get('recentProjects', {})!;
  }
  const prev = storage[accountId];
  RecentProjectStorage.set('recentProjects', {
    ...storage,
    [accountId]: setter(prev),
  });
};

const checkStorageVersion = () => {
  const storage = RecentProjectStorage.get('recentProjects', {});
  if (storage && storage[STORAGE_VERSION_KEY]?.[0] !== STORAGE_VERSION) {
    RecentProjectStorage.set('recentProjects', {
      [STORAGE_VERSION_KEY]: [STORAGE_VERSION],
    });
  }
};

const STORAGE_VERSION_KEY = 'VERSION';
const STORAGE_VERSION = '1';
